// Front-end Stripe environment selector.
//
// Decides whether the browser should talk to Stripe in `test` or `live` mode
// and exposes the matching publishable key. The same `mode` string is sent
// alongside every payment-related Cloud Function call so the server picks the
// matching secret key. Keep this module the single source of truth.
//
// Selection rules:
//   1. `REACT_APP_STRIPE_MODE` env (`test` | `live`) wins if it's set.
//   2. Otherwise → `test` in development / `live` in production builds.
//
// Configure publishable keys in `.env`:
//   REACT_APP_STRIPE_KEY_TEST=pk_test_...
//   REACT_APP_STRIPE_KEY_LIVE=pk_live_...
//
// IMPORTANT: this helper never silently falls back from one mode to the other.
// If you ask for `test` and the test key isn't configured, we return an empty
// string (and `getKeyError()` returns a human message). That avoids the
// `live_mode_test_card` Stripe decline you'd otherwise hit when a test card
// gets tokenized with a live publishable key.

const VALID_MODES = ["test", "live"];

const ENV_VAR_FOR_MODE = {
  test: "REACT_APP_STRIPE_KEY_TEST",
  live: "REACT_APP_STRIPE_KEY_LIVE",
};

export const getStripeMode = () => {
  const explicit = (process.env.REACT_APP_STRIPE_MODE || "").toLowerCase();
  if (VALID_MODES.includes(explicit)) return explicit;
  return process.env.NODE_ENV === "production" ? "live" : "test";
};

export const getStripePublishableKey = () => {
  const mode = getStripeMode();
  const envVar = ENV_VAR_FOR_MODE[mode];
  return process.env[envVar] || "";
};

/**
 * Returns null when the publishable key for the active mode is configured,
 * otherwise a short human-readable message so the UI can show / log it.
 */
export const getKeyError = () => {
  const mode = getStripeMode();
  if (getStripePublishableKey()) return null;
  return `Stripe publishable key for "${mode}" mode is missing. Set ${ENV_VAR_FOR_MODE[mode]} in your .env and restart the dev server.`;
};
